/**
 * Shared conversation types for listeners and talkers.
 * Warm, non-clinical. Not therapy, counseling, crisis support, or professional expertise.
 *
 * publicData.topics (listing) and publicData.interestedTopics (talker) store canonical ids.
 * Optional custom text for "Other" lives in publicData.topicsOther / interestedTopicsOther.
 * Legacy ids from the first TEST taxonomy are aliased so saved profiles stay compatible.
 */

export const OTHER_TOPIC_ID = 'other';

/**
 * Interests (Oct 14): things to talk ABOUT. Stored in the same publicData.topics /
 * interestedTopics arrays as conversation topics so search, booking and matching
 * share one taxonomy; UI renders them as a separate "Interests" group.
 */
const INTEREST_IDS_LIST = [
  'sports',
  'tv-movies',
  'binge-shows',
  'music',
  'books',
  'gaming',
  'pets',
  'cooking-food',
  'travel',
  'cars',
  'fitness',
  'outdoors',
  'faith-church',
  'art-crafts',
  'tech',
  'true-crime',
  'fashion-beauty',
  'gardening',
  'history',
  'comedy',
  'news-current-events',
];
export const INTEREST_IDS = INTEREST_IDS_LIST;
export const FAVORITE_TEAMS_MAX_LENGTH = 80;
export const TOPICS_OTHER_MAX_LENGTH = 80;

export const SESSION_TOPIC_IDS = [
  'stress-venting',
  'relationships-dating',
  'dating',
  'family',
  'friendship',
  'loneliness',
  'work-career',
  'school-student',
  'parenting',
  'caregiving',
  'grief-loss',
  'major-life-changes',
  'confidence-self-esteem',
  'identity-belonging',
  'everyday-conversation',
  'advice-perspective',
  'hobbies-interests',
  'celebrations-good-news',
  'spirituality-meaning',
  'mentorship',
  'career-advice',
  'money-worries',
  'divorce-separation',
  'breakups',
  'women-supporting-women',
  'womens-issues',
  'new-in-town',
  ...INTEREST_IDS_LIST,
  OTHER_TOPIC_ID,
];

/**
 * First-generation TEST ids → current taxonomy.
 * Search, display, and the next save all canonicalize through this map.
 */
export const LEGACY_TOPIC_ALIASES = {
  vent: 'stress-venting',
  'talk-through': 'advice-perspective',
  lonely: 'loneliness',
  relationships: 'relationships-dating',
  work: 'work-career',
  encouragement: 'confidence-self-esteem',
  easygoing: 'everyday-conversation',
  quiet: 'everyday-conversation',
  hobbies: 'hobbies-interests',
  'advice-listening': 'advice-perspective',
};

export const SESSION_TOPIC_LABELS = {
  'stress-venting': 'Stress or venting',
  'relationships-dating': 'Relationships',
  dating: 'Dating',
  family: 'Family',
  friendship: 'Friendship',
  loneliness: 'Wanting company',
  'work-career': 'Work or career',
  'school-student': 'School or student life',
  parenting: 'Parenting',
  caregiving: 'Caregiving',
  'grief-loss': 'Grief & loss',
  'major-life-changes': 'Big life changes',
  'confidence-self-esteem': 'Confidence or self-esteem',
  'identity-belonging': 'Identity or belonging',
  'everyday-conversation': 'Everyday conversation',
  'advice-perspective': 'Advice or perspective',
  'hobbies-interests': 'Hobbies or interests',
  'celebrations-good-news': 'Celebrations or good news',
  'spirituality-meaning': 'Faith or spirituality',
  mentorship: 'Mentorship',
  'career-advice': 'Career advice',
  'money-worries': 'Money stress',
  'divorce-separation': 'Divorce & separation',
  breakups: 'Breakups',
  'women-supporting-women': 'Women supporting women',
  'womens-issues': "Women's issues",
  'new-in-town': 'Relocation / New in town',
  sports: 'Sports',
  'tv-movies': 'TV & movies',
  'binge-shows': 'Netflix & binge-worthy shows',
  music: 'Music',
  books: 'Books',
  gaming: 'Gaming',
  pets: 'Pets',
  'cooking-food': 'Cooking & food',
  travel: 'Travel',
  cars: 'Cars',
  fitness: 'Fitness',
  outdoors: 'Outdoors',
  'faith-church': 'Faith & church life',
  'art-crafts': 'Art & crafts',
  tech: 'AI & tech',
  'true-crime': 'True crime',
  'fashion-beauty': 'Fashion & beauty',
  gardening: 'Gardening',
  history: 'History',
  comedy: 'Comedy',
  'news-current-events': 'World events',
  [OTHER_TOPIC_ID]: 'Other',
};

export const SESSION_TOPICS = SESSION_TOPIC_IDS.map(id => ({
  id,
  label: SESSION_TOPIC_LABELS[id],
  messageId: `SessionTopics.${id}`,
}));

export const TOPICS_PARAM = 'topics';

export const canonicalizeTopicId = value => {
  const raw = String(value || '').trim();
  if (!raw) {
    return null;
  }
  if (SESSION_TOPIC_IDS.includes(raw)) {
    return raw;
  }
  return LEGACY_TOPIC_ALIASES[raw] || null;
};

export const isSessionTopicId = value => canonicalizeTopicId(value) != null;

export const normalizeTopics = value => {
  const list = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split(',')
    : [];
  const seen = new Set();
  return list
    .map(item => canonicalizeTopicId(item))
    .filter(id => {
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
};

export const normalizeTopicsOther = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.replace(/\s+/g, ' ').trim().slice(0, TOPICS_OTHER_MAX_LENGTH);
};

export const topicsOtherForSave = (topics, otherText) => {
  const ids = normalizeTopics(topics);
  if (!ids.includes(OTHER_TOPIC_ID)) {
    return null;
  }
  const custom = normalizeTopicsOther(otherText);
  return custom || null;
};

export const parseTopicsParam = value => normalizeTopics(value);

export const topicsFromListing = listing =>
  normalizeTopics(listing?.attributes?.publicData?.topics || listing?.publicData?.topics);

/**
 * Listening profiles need at least one conversation type before Next / publish.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const hasRequiredListeningTopics = value => normalizeTopics(value).length > 0;

/**
 * Topics from a form payload or listing update body.
 *
 * @param {Object} [data]
 * @returns {string[]}
 */
export const topicsFromListingData = (data = {}) =>
  normalizeTopics(data?.topics ?? data?.publicData?.topics);

export const topicsOtherFromListing = listing =>
  normalizeTopicsOther(
    listing?.attributes?.publicData?.topicsOther || listing?.publicData?.topicsOther
  );

export const listingOffersTopics = (listing, wanted) => {
  const need = normalizeTopics(wanted);
  if (need.length === 0) {
    return true;
  }
  const have = topicsFromListing(listing);
  if (have.length === 0) {
    return true;
  }
  return need.some(id => have.includes(id));
};

export const filterListingsByTopics = (listings = [], wanted) => {
  const need = normalizeTopics(wanted);
  if (need.length === 0) {
    return listings;
  }
  return listings.filter(listing => listingOffersTopics(listing, need));
};

const aliasHaystackFor = ids =>
  Object.entries(LEGACY_TOPIC_ALIASES)
    .filter(([, canonical]) => ids.includes(canonical))
    .map(([legacy]) => legacy.replace(/-/g, ' '));

export const topicSearchHaystack = (topics, otherText, teams) => {
  const ids = normalizeTopics(topics);
  const labels = ids.map(id => SESSION_TOPIC_LABELS[id] || id);
  const custom = normalizeTopicsOther(otherText);
  return [...ids, ...labels, ...aliasHaystackFor(ids), custom, normalizeFavoriteTeams(teams)]
    .filter(Boolean)
    .join(' ');
};

export const topicChipLabel = (id, otherText) => {
  const canonical = canonicalizeTopicId(id);
  if (!canonical) {
    return '';
  }
  if (canonical === OTHER_TOPIC_ID) {
    return normalizeTopicsOther(otherText) || SESSION_TOPIC_LABELS[OTHER_TOPIC_ID];
  }
  return SESSION_TOPIC_LABELS[canonical] || canonical;
};

export const isInterestId = id => INTEREST_IDS.includes(id);
export const CONVERSATION_TOPIC_IDS = SESSION_TOPIC_IDS.filter(id => !isInterestId(id));
const toOption = id => ({ id, label: SESSION_TOPIC_LABELS[id], messageId: `SessionTopics.${id}` });
export const CONVERSATION_TOPICS = CONVERSATION_TOPIC_IDS.map(toOption);
export const INTERESTS = INTEREST_IDS.map(toOption);
/** Option list with group headers for pickers: { header: true, id, messageId }. */
export const SESSION_TOPICS_GROUPED = [
  { header: true, id: '__topics', messageId: 'SessionTopics.groupTopics' },
  ...CONVERSATION_TOPIC_IDS.filter(id => id !== OTHER_TOPIC_ID).map(toOption),
  { header: true, id: '__interests', messageId: 'SessionTopics.groupInterests' },
  ...INTERESTS,
  toOption(OTHER_TOPIC_ID),
];

export const normalizeFavoriteTeams = value =>
  typeof value === 'string'
    ? value.replace(/\s+/g, ' ').trim().slice(0, FAVORITE_TEAMS_MAX_LENGTH)
    : '';

export const favoriteTeamsFromListing = listing =>
  normalizeFavoriteTeams(
    listing?.attributes?.publicData?.favoriteTeams || listing?.publicData?.favoriteTeams
  );

export const favoriteTeamsForSave = (topics, teams) =>
  normalizeTopics(topics).includes('sports') ? normalizeFavoriteTeams(teams) || null : null;

/**
 * Overlap score between a talker's interests/topics and a listing's.
 * 'other' and generic ids don't count.
 */
export const topicOverlap = (talkerTopics, listing) => {
  const mine = normalizeTopics(talkerTopics).filter(id => id !== OTHER_TOPIC_ID);
  if (!mine.length) return { score: 0, shared: [] };
  const have = topicsFromListing(listing);
  const shared = mine.filter(id => have.includes(id));
  return { score: shared.length, shared };
};

/**
 * Rank listings for "We think you'd like these listeners".
 * Higher overlap first; Available Now first among ties.
 */
export const rankListingsByOverlap = (listings = [], talkerTopics, presenceMap = {}, limit = 6) =>
  listings
    .map((listing, index) => {
      const { score, shared } = topicOverlap(talkerTopics, listing);
      const id = listing?.id?.uuid;
      return { listing, score, shared, online: !!(id && presenceMap[id]), index };
    })
    .filter(row => row.score > 0)
    .sort((a, b) => b.score - a.score || Number(b.online) - Number(a.online) || a.index - b.index)
    .slice(0, limit);

/** Selectable but never featured (no home chips, topic pages, sitemap or ads). */
export const UNFEATURED_TOPIC_IDS = ['grief-loss', 'caregiving'];

// One Earning profile (Sam, Oct 24 2026, TEST): Listening and Teaching are
// both optional sections, but a profile must offer at least one of them.
export const isListeningSkipped = publicData => publicData?.listeningSkipped === true;
export const hasTeachingOffers = publicData =>
  Array.isArray(publicData?.skillOffers) &&
  publicData.skillOffers.some(o => o && o.id && o.id !== 'listening');
/** Details-step check: topics, or Listening skipped (Teaching comes next). */
export const hasListeningTopicsOrSkipped = publicData =>
  isListeningSkipped(publicData) || hasRequiredListeningTopics(publicData?.topics);
/** Publish check: offers Listening (with topics) or at least one skill. */
export const hasProfileOffer = publicData =>
  isListeningSkipped(publicData)
    ? hasTeachingOffers(publicData)
    : hasRequiredListeningTopics(publicData?.topics);
